/**
 * NavGlyph — renders a clean Feather line-icon for a nav/search item.
 *
 * The nav + spotlight data (constants.js NAV_SECTIONS, searchIndex) still carry
 * emoji strings in their `icon` field — that data shape is shared by several
 * consumers, so rather than change it everywhere we map the emoji to a Feather
 * icon at RENDER time here. Unmapped glyphs (e.g. document/resolution content
 * icons) fall back to the original emoji, so nothing ever renders blank.
 *
 * Keep this in sync with the icons used in constants.js NAV_SECTIONS /
 * ADMIN_NAV_SECTIONS when nav items are added.
 */
import {
  RiHome5Fill, RiBriefcaseFill, RiNotification3Fill, RiBarChart2Fill, RiStackFill, RiGroupFill, RiStockFill, RiUser3Fill,
  RiHeartFill, RiCpuFill, RiFileTextFill, RiBookOpenFill, RiFolder5Fill, RiClipboardFill, RiArchiveDrawerFill, RiCalendarTodoFill,
  RiFileFill, RiShoppingBagFill, RiMoneyDollarCircleFill, RiSearch2Fill, RiLockFill, RiShieldFill, RiSettings3Fill, RiBox3Fill,
  RiHammerFill, RiKeyFill, RiFlagFill, RiCustomerService2Fill, RiTicket2Fill,
} from 'react-icons/ri';

// Exported so the /design-system reference can render the full sidebar/nav
// icon set alongside the Icon-kit glyphs.
export const ICON_FOR_EMOJI = {
  '🏠': RiHome5Fill,
  '🏢': RiBriefcaseFill,
  '🔔': RiNotification3Fill,
  '📊': RiBarChart2Fill,
  '🏗': RiStackFill,
  '🤝': RiGroupFill,
  '📈': RiStockFill,
  '👤': RiUser3Fill,
  '🌱': RiHeartFill,
  '🤖': RiCpuFill,
  '📋': RiFileTextFill,
  '📜': RiBookOpenFill,
  '🗂': RiFolder5Fill,
  '🧾': RiClipboardFill,
  '📂': RiArchiveDrawerFill,
  '👥': RiGroupFill,
  '📅': RiCalendarTodoFill,
  '🗓': RiCalendarTodoFill,
  '📆': RiCalendarTodoFill,
  '📑': RiFileFill,
  '🏬': RiShoppingBagFill,
  '💰': RiMoneyDollarCircleFill,
  '🔍': RiSearch2Fill,
  '🔒': RiLockFill,
  '🛡': RiShieldFill,
  '⚙': RiSettings3Fill,
  '📦': RiBox3Fill,
  '🔧': RiHammerFill,
  '🔐': RiKeyFill,
  '🚩': RiFlagFill,
  '🎧': RiCustomerService2Fill,
  '🎫': RiTicket2Fill,
};

// Emoji variation selector — appended to some glyphs (shield, gear). Stripped so
// "🛡 + VS16" matches the bare "🛡" key above. Built from its code point to keep
// this source free of invisible characters.
const VS16 = String.fromCharCode(0xfe0f);

export default function NavGlyph({ icon, size = 16, className, style }) {
  const key = typeof icon === 'string' ? icon.split(VS16).join('') : icon;
  const Cmp = ICON_FOR_EMOJI[key] || RiFileTextFill;
  // Always render a line icon — never a raw emoji — so the icon language stays
  // consistent. Unmapped content glyphs default to a neutral document icon.
  return <Cmp size={size} className={className} style={style} aria-hidden="true" />;
}
